import { useState, useEffect, useCallback } from 'react';
import { useCommonPageContext } from '@atlassian/jira-servicedesk-common-page-context';
import {
	VIEW_MODE,
	COLLAPSED_MODE,
	EDIT_MODE,
	NEW_MODE,
	COPY_MODE,
	CLONE_MODE,
} from '../../../common/constants';
import type { COPY_OR_CLONE_MODE } from '../../../common/types';
import { useSLAConfigActionsFlags } from '../../../common/ui/sla-config-action-flags';
import type { OtherProjectCalendar, Calendar } from '../../../common/utils/calendars/types';
import { useFormContext } from '../../../controllers/form/index.tsx';
import { useMetricResource } from '../../../controllers/metric-resource';
import { useWorkingHoursService } from '../../../services/working-hours/main.tsx';
import type { CalendarsModalStateAndActions } from './types';
import { transformToCalendarRefs } from './utils';

export const useCalendarsModalStateAndActions = (
	isCalendarModalOpen: boolean,
	onToggleCalendarModal: () => void,
): CalendarsModalStateAndActions => {
	const { baseUrl, projectKey } = useCommonPageContext();
	const [selectedCalendarId, setSelectedCalendarId] = useState<unknown>();
	const [selectedCalendarMode, setSelectedCalendarMode] = useState(VIEW_MODE);
	const [permissionsWithTimezoneInfo, setPermissionsWithTimezoneInfo] = useState<unknown>();
	const [calendars, setCalendars] = useState<unknown>();
	// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
	const [{ data, loading, error }, { fetch }] = useWorkingHoursService(baseUrl, projectKey);
	const [{ data: metricsResource }, { update: updateCalendarsDataInMetricResource }] =
		useMetricResource();
	const [{ showNavBlocker }, { setNextCalendarActionCallback }] = useFormContext();
	const [copyCalendarData, setCopyCalendarData] = useState<unknown>(); // state for holding calendars from both the current and other service desk projects
	const { calendarSaveSuccessFlag, calendarDeleteSuccessFlag } = useSLAConfigActionsFlags();

	useEffect(() => {
		const fetchData = async () => {
			if (isCalendarModalOpen && !data && !loading) {
				await fetch();
			}
		};
		fetchData();
	}, [calendars, data, fetch, isCalendarModalOpen, loading]);

	useEffect(() => {
		if (data) {
			const { calendars: calendarData, ...permissionsAndTimezoneData } = data;
			setCalendars(calendarData);
			setPermissionsWithTimezoneInfo(permissionsAndTimezoneData);
		}
	}, [data]);

	// Metric list uses calendarRefs resource data
	// on successful update to the calendar update the resource to reflect the same in metrics
	const updateCalendarsData = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'updatedCalendarList' implicitly has an 'any' type.
		(updatedCalendarList) => {
			setCalendars(updatedCalendarList);
			updateCalendarsDataInMetricResource((previous) => ({
				...previous,
				calendarRefs: transformToCalendarRefs(updatedCalendarList, metricsResource?.calendarRefs),
			}));
		},
		[metricsResource, updateCalendarsDataInMetricResource],
	);

	const resetNextCalendarActionCallback = useCallback(() => {
		// Replace with lodash/noop
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		setNextCalendarActionCallback(() => {});
	}, [setNextCalendarActionCallback]);

	const onToggleViewRequestedHelper = useCallback(
		(calendarId?: number) => {
			const shouldCollapse =
				selectedCalendarId === calendarId && selectedCalendarMode !== COLLAPSED_MODE;
			if (shouldCollapse) {
				setSelectedCalendarId(undefined);
				setSelectedCalendarMode(COLLAPSED_MODE);
			} else {
				setSelectedCalendarId(calendarId);
				setSelectedCalendarMode(VIEW_MODE);
			}
		},
		[selectedCalendarId, selectedCalendarMode],
	);

	// onToggleViewRequested verify whether form is dirty or not
	// if form is dirty show confirmation modal
	const onToggleViewRequested = useCallback(
		(calendarId?: number) => {
			if (showNavBlocker) {
				// This would add the onToggleViewRequested method to the calendar callback
				setNextCalendarActionCallback(() => {
					onToggleViewRequestedHelper(calendarId);
					resetNextCalendarActionCallback();
				});
				showNavBlocker();
			} else {
				onToggleViewRequestedHelper(calendarId);
			}
		},
		[
			showNavBlocker,
			setNextCalendarActionCallback,
			onToggleViewRequestedHelper,
			resetNextCalendarActionCallback,
		],
	);

	const onEditRequestedHelper = useCallback((calendarId: number) => {
		setSelectedCalendarId(calendarId);
		setSelectedCalendarMode(EDIT_MODE);
	}, []);

	// onEditRequested verify whether form is dirty or not
	// if form is dirty show confirmation modal
	const onEditRequested = useCallback(
		(calendarId: number) => {
			// if nav blocker is in place assume app has a nav blocker in place
			// nav blocker needs to be displayed on change mode actions
			if (showNavBlocker) {
				// This would add the onEditRequested method to the calendar callback
				setNextCalendarActionCallback(() => {
					onEditRequestedHelper(calendarId);
					resetNextCalendarActionCallback();
				});
				showNavBlocker();
			} else {
				onEditRequestedHelper(calendarId);
			}
		},
		[
			onEditRequestedHelper,
			resetNextCalendarActionCallback,
			setNextCalendarActionCallback,
			showNavBlocker,
		],
	);

	const onCreateNewRequestedHelper = useCallback(() => {
		setSelectedCalendarId(undefined);
		setSelectedCalendarMode(NEW_MODE);
	}, []);

	// onCreateNewRequested verify whether form is dirty or not
	// if form is dirty show confirmation modal
	const onCreateNewRequested = useCallback(() => {
		if (showNavBlocker) {
			// This would add the onCreateNewRequested method to the calendar callback
			setNextCalendarActionCallback(() => {
				onCreateNewRequestedHelper();
				resetNextCalendarActionCallback();
			});
			showNavBlocker();
		} else {
			onCreateNewRequestedHelper();
		}
	}, [
		onCreateNewRequestedHelper,
		resetNextCalendarActionCallback,
		setNextCalendarActionCallback,
		showNavBlocker,
	]);

	// onEditCalendarSuccessful is invoked after modifyCalendar Async is successful
	const onEditCalendarSuccessful = useCallback(
		(modifiedCalendar: Calendar) => {
			// @ts-expect-error - TS2488 - Type 'unknown' must have a '[Symbol.iterator]()' method that returns an iterator.
			const updatedCalendarList = [...(calendars || [])];
			const modifiedIndex = updatedCalendarList.findIndex(({ id }) => id === modifiedCalendar.id);
			//  for new calendar en try append the data to calendar refs
			//  else replace the calendar data
			if (modifiedIndex === -1) {
				updatedCalendarList.push(modifiedCalendar);
			} else {
				updatedCalendarList.splice(modifiedIndex, 1, modifiedCalendar);
			}
			setSelectedCalendarMode(VIEW_MODE);
			updateCalendarsData(updatedCalendarList);
			calendarSaveSuccessFlag(modifiedCalendar.name, modifiedIndex === -1);
		},
		[calendars, updateCalendarsData, calendarSaveSuccessFlag],
	);

	// onDeleteCalendarSuccessful is invoked after deleteCalendar Async is successful
	const onDeleteCalendarSuccessful = useCallback(
		(calendarID: number) => {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			const deletedCalendar = calendars?.find((calendar: Calendar) => calendar.id === calendarID);
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			const updatedCalendarList = calendars?.filter(
				(calendar: Calendar) => calendarID !== calendar.id,
			);
			setCalendars(updatedCalendarList);
			updateCalendarsData(updatedCalendarList);
			deletedCalendar && calendarDeleteSuccessFlag(deletedCalendar.name);
		},
		[calendars, updateCalendarsData, calendarDeleteSuccessFlag],
	);

	// onReset reset the state to defaults
	const onReset = useCallback(() => {
		setSelectedCalendarId(undefined);
		setSelectedCalendarMode(VIEW_MODE);
	}, []);

	const onToggleCalendarModalWithNavBlocker = useCallback(async () => {
		if (showNavBlocker) {
			setNextCalendarActionCallback(() => {
				setSelectedCalendarId(undefined);
				setSelectedCalendarMode(COLLAPSED_MODE);
				onToggleCalendarModal(); // Add the next callback
				resetNextCalendarActionCallback();
			});
			showNavBlocker();
		} else {
			onToggleCalendarModal();
		}
	}, [
		showNavBlocker,
		setNextCalendarActionCallback,
		onToggleCalendarModal,
		resetNextCalendarActionCallback,
	]);

	const setCopyOrCloneCalendarDataUtil = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(calendar: any, mode: COPY_OR_CLONE_MODE) => {
			setSelectedCalendarMode(mode);
			setSelectedCalendarId(undefined);
			setCopyCalendarData(calendar);
		},
		[],
	);

	// This is for copying calendars from other service desk projects
	const onCopyCalendar = useCallback(
		(copyCalendar: OtherProjectCalendar) => {
			setCopyOrCloneCalendarDataUtil(copyCalendar, COPY_MODE);
		},
		[setCopyOrCloneCalendarDataUtil],
	);

	const onCloneCalendar = useCallback(
		(copyCalendar: Calendar) => {
			setCopyOrCloneCalendarDataUtil(copyCalendar, CLONE_MODE);
		},
		[setCopyOrCloneCalendarDataUtil],
	);

	const state = {
		selectedCalendarId,
		selectedCalendarMode,
		permissionsWithTimezoneInfo,
		calendars,
		isLoading: loading,
		isError: Boolean(error),
		copyCalendarData,
	};

	const actions = {
		onToggleViewRequested,
		onEditRequested,
		onCreateNewRequested,
		onEditCalendarSuccessful,
		onDeleteCalendarSuccessful,
		onReset,
		onToggleCalendarModalWithNavBlocker,
		onCopyCalendar,
		onCloneCalendar,
	};

	// @ts-expect-error - TS2322 - Type '{ selectedCalendarId: unknown; selectedCalendarMode: string; permissionsWithTimezoneInfo: unknown; calendars: unknown; isLoading: boolean; isError: boolean; copyCalendarData: unknown; }' is not assignable to type 'CalendarsModalState'.
	return [state, actions];
};
